import { Box, type SxProps, type Theme } from "@mui/material";
import { useEffect, useRef } from "react";

/** 화면 이만큼 앞에서 받기 시작한다 */
const NEAR_ROOT_MARGIN = "600px";

const svgCache = new Map<string, Promise<string>>();

/** 사이트 미디어의 SVG 를 받아 스크립트·이벤트 속성을 걷어낸 마크업으로 돌려준다(같은 주소는 한 번만 받는다). */
const loadSvgMarkup = (url: string) => {
  let pending = svgCache.get(url);
  if (!pending) {
    pending = fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`svg ${response.status}`);
        return response.text();
      })
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, "image/svg+xml");
        const svg = doc.documentElement;
        if (svg.nodeName.toLowerCase() !== "svg") throw new Error("not svg");
        svg
          .querySelectorAll("script, foreignObject")
          .forEach((node) => node.remove());
        svg.querySelectorAll("*").forEach((node) =>
          [...node.attributes].forEach((attribute) => {
            if (attribute.name.startsWith("on"))
              node.removeAttribute(attribute.name);
          })
        );
        return svg.outerHTML;
      });
    svgCache.set(url, pending);
  }
  return pending;
};

export interface InlineSvgProps {
  url: string;
  sx?: SxProps<Theme>;
}

/**
 * 사이트 미디어의 그림 SVG 를 문서 안에 직접 넣는다 — `<img>` 로 넣으면 그림 속 글자가 사이트 글꼴(Pretendard)을 못 써서
 * 시안과 달라지기 때문이다. 화면 가까이 와야 받고, 받기 전에는 부모의 바탕색이 칸을 채운다.
 * 원본은 우리 미디어 저장소의 정적 그림이며, 넣기 전에 스크립트·이벤트 속성을 걷어낸다.
 */
export function InlineSvg({ url, sx }: InlineSvgProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;
    let cancelled = false;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        loadSvgMarkup(url)
          .then((markup) => {
            if (!cancelled) host.innerHTML = markup;
          })
          .catch(() => {});
      },
      { rootMargin: NEAR_ROOT_MARGIN }
    );
    observer.observe(host);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [url]);
  return <Box ref={hostRef} aria-hidden="true" sx={sx} />;
}
